import React, { useState, useEffect } from 'react';
import './AddMemberModal.css';

function AddMemberModal({ onClose, onMemberAdded, networkNodes }) {
  const [formData, setFormData] = useState({
    mentor_id: '',
    first_name: '',
    last_name: '',
    network_id: '',
    country: '',
    city: '',
    notes: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isManualMode, setIsManualMode] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [companyName, setCompanyName] = useState('Network Company');

  //Fetch company name and contacts on mount
  useEffect(() => {
    fetchCompanyName();
    fetchContacts();
  }, []);

  const fetchCompanyName = async () => {
    try {
      const response = await fetch('/backend/api/admin/get-company-name.php', {
        credentials: 'include'
      });
      const data = await response.json();
      if (data.success && data.companyName) {
        setCompanyName(data.companyName);
      }
    } catch (error) {
      console.error('Error fetching company name:', error);
    }
  };

  const fetchContacts = async () => {
    try {
      const response = await fetch('/backend/api/contacts/list.php', {
        credentials: 'include'
      });
      const data = await response.json();
      if (data.success && data.contacts) {
        setContacts(data.contacts);
      }
    } catch (error) {
      console.error('Error fetching contacts:', error);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleContactSelect = (e) => {
    const contactId = e.target.value;
    setSelectedContact(contactId);
    
    if (contactId) {
      const contact = contacts.find(c => c.id === parseInt(contactId));
      if (contact) {
        setFormData({
          ...formData,
          first_name: contact.first_name,
          last_name: contact.last_name,
          country: contact.country || '',
          city: contact.city || ''
        });
      }
    } else {
      // Clear fields if no contact selected
      setFormData({
        ...formData,
        first_name: '',
        last_name: '',
        country: '',
        city: ''
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // In contact mode, ensure we have contact data
    if (!isManualMode && !selectedContact) {
      setError('Please select a contact');
      setLoading(false);
      return;
    }

    // Validate required fields
    if (!formData.first_name || !formData.last_name) {
      setError('First name and last name are required');
      setLoading(false);
      return;
    }

    // Validate network_id is provided (now required)
    if (!formData.network_id || formData.network_id.trim() === '') {
      setError(`${companyName} ID is required`);
      setLoading(false);
      return;
    }

    try {
      // Prepare data for submission
      const submitData = {
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim()
      };

      // Parse mentor selection (format: "user_123" or "temp_45")
      if (formData.mentor_id) {
        const mentorValue = formData.mentor_id;
        if (mentorValue.startsWith('user_')) {
          submitData.mentor_id = parseInt(mentorValue.replace('user_', ''));
          submitData.mentor_type = 'user';
        } else if (mentorValue.startsWith('temp_')) {
          submitData.mentor_id = parseInt(mentorValue.replace('temp_', ''));
          submitData.mentor_type = 'temp';
        }
      }

      // Add optional fields if provided
      if (formData.network_id) submitData.network_id = formData.network_id.trim();
      if (formData.country) submitData.country = formData.country.trim();
      if (formData.city) submitData.city = formData.city.trim();
      if (formData.notes) submitData.notes = formData.notes.trim();

      const response = await fetch('/backend/api/network/add-temporary.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(submitData)
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const data = await response.json();
      if (data.success) {
        onMemberAdded();
        onClose();
      } else {
        setError(data.error || 'Failed to add member');
      }
    } catch (error) {
      console.error('Error adding member:', error);
      setError('Failed to add member');
    } finally {
      setLoading(false);
    }
  };

  // Filter user and temporary nodes for mentor selection
  // Exclude current user (level 0) from the list since they're already "You (default)"
  const userNodes = networkNodes.filter(node => node.type === 'user' && node.level !== 0);
  const tempNodes = networkNodes.filter(node => node.type === 'temporary');

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="add-member-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Add Team Member</h2>
          <button onClick={onClose} className="close-btn">×</button>
        </div>

        <form onSubmit={handleSubmit} className="modal-form">
          {error && <div className="form-error">{error}</div>}

          {/* Mode Toggle - iOS Style */}
          <div className="form-mode-toggle">
            <div className="toggle-container">
              <span className="toggle-label-text">Add Manually</span>
              <label className="ios-toggle">
                <input
                  type="checkbox"
                  checked={isManualMode}
                  onChange={(e) => {
                    setIsManualMode(e.target.checked);
                    if (!e.target.checked) {
                      // Clear fields when switching to contact mode
                      setSelectedContact(null);
                      setFormData({ ...formData, first_name: '', last_name: '', country: '', city: '' });
                    }
                  }}
                />
                <span className="ios-toggle-slider"></span>
              </label>
            </div>
            <p className="mode-description">
              {isManualMode ? 'Enter all details manually' : 'Select from your contacts'}
            </p>
          </div>

          <div className="form-group">
            <label htmlFor="mentor_id">Mentor (Optional)</label>
            <select
              id="mentor_id"
              name="mentor_id"
              value={formData.mentor_id}
              onChange={handleChange}
              className="form-select"
            >
              <option value="">You (default)</option>
              {userNodes.map(node => (
                <option key={node.id} value={node.id}>
                  {node.name} (Level {node.level})
                </option>
              ))}
              {tempNodes.length > 0 && (
                <option disabled>-- Temporary Users --</option>
              )}
              {tempNodes.map(node => (
                <option 
                  key={node.id} 
                  value={node.id}
                  className="temp-mentor-option"
                >
                  {node.name} (Level {node.level}) 🔗
                </option>
              ))}
            </select>
            <small className="form-hint">Select who will mentor this person</small>
          </div>

          {/* Contact Selection Mode */}
          {!isManualMode ? (
            <div className="form-group">
              <label htmlFor="contact_id">Select Contact *</label>
              <select
                id="contact_id"
                value={selectedContact || ''}
                onChange={handleContactSelect}
                className="form-select"
                required
              >
                <option value="">Choose a contact...</option>
                {contacts.map(contact => (
                  <option key={contact.id} value={contact.id}>
                    {contact.first_name} {contact.last_name}
                  </option>
                ))}
              </select>
              <small className="form-hint">
                {contacts.length === 0 ? 'No contacts available - switch to manual mode' : 'Select an existing contact'}
              </small>
            </div>
          ) : (
            /* Manual Entry Mode */
            <>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="first_name">First Name *</label>
                  <input
                    type="text"
                    id="first_name"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleChange}
                    className="form-input"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="last_name">Last Name *</label>
                  <input
                    type="text"
                    id="last_name"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleChange}
                    className="form-input"
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="city">City</label>
                  <input
                    type="text"
                    id="city"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    className="form-input"
                    placeholder="e.g., Tallinn"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="country">Country</label>
                  <input
                    type="text"
                    id="country"
                    name="country"
                    value={formData.country}
                    onChange={handleChange}
                    className="form-input"
                    placeholder="e.g., Estonia"
                  />
                </div>
              </div>
            </>
          )}

          {/* Network Company ID - always shown, always required */}
          <div className="form-group">
            <label htmlFor="network_id">{companyName} ID *</label>
            <input
              type="text"
              id="network_id"
              name="network_id"
              value={formData.network_id}
              onChange={handleChange}
              className="form-input"
              placeholder="e.g., ABC123"
              required
            />
            <small className="form-hint">
              When they register with this ID, they'll be auto-linked
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="notes">Notes</label>
            <textarea
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              className="form-textarea"
              rows="3"
              placeholder="Optional notes about this person..."
            ></textarea>
          </div>

          <div className="form-actions">
            <button
              type="button"
              onClick={onClose}
              className="btn-cancel"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-submit"
              disabled={loading}
            >
              {loading ? 'Adding...' : 'Add Member'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AddMemberModal;

